import {
  Users
} from 'lucide-react';
import { useEffect, useRef, useState } from "react";
import {
  FaQuestionCircle,
} from "react-icons/fa";
import { GoGear } from "react-icons/go";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";
import { IoPersonOutline, IoStatsChartOutline } from "react-icons/io5";
import { PiRobot } from "react-icons/pi";
import { TbSchool } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import SupportPopup from "../04-SUPPORT-PAGE/SupportPopup";

// Composant Tooltip style Pennylane
const Tooltip = ({ text, targetRef, show, isTab = false }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (show && targetRef?.current) {
      const rect = targetRef.current.getBoundingClientRect();

      if (isTab) {
        // Pour les onglets, trouver la sidebar et positionner à son bord droit
        const sidebar = document.querySelector('[data-sidebar]');
        if (sidebar) {
          const sidebarRect = sidebar.getBoundingClientRect();
          setPosition({
            top: rect.top + rect.height / 2,
            left: sidebarRect.right + 8
          });
        } else {
          // Fallback si pas de sidebar trouvée
          setPosition({
            top: rect.top + rect.height / 2,
            left: rect.right + 8
          });
        }
      } else {
        // Position normale pour les autres éléments
        setPosition({
          top: rect.top + rect.height / 2,
          left: rect.right + 8
        });
      }
    }
  }, [show, targetRef, isTab]);

  if (!show) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        transform: 'translateY(-50%)',
        zIndex: 9999,
        pointerEvents: 'none'
      }}
    >
      <div style={{
        backgroundColor: '#1f2937',
        color: 'white',
        fontSize: '12px',
        fontWeight: '500',
        padding: '4px 8px',
        borderRadius: '4px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        whiteSpace: 'nowrap'
      }}>
        {text}
        <div
          style={{
            position: 'absolute',
            right: '100%',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 0,
            height: 0,
            borderTop: '4px solid transparent',
            borderRight: '4px solid #1f2937',
            borderBottom: '4px solid transparent'
          }}
        />
      </div>
    </div>
  );
};

// Zone de redimensionnement avec curseur personnalisé
const ResizeHandle = ({ collapsed, onToggle }) => {
  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        height: '100%',
        width: '8px',
        cursor: collapsed ? 'e-resize' : 'w-resize',
        zIndex: 10,
        backgroundColor: 'transparent'
      }}
      onClick={onToggle}
    />
  );
};

function Sidebar() {
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState(false);
  const [showSupportPopup, setShowSupportPopup] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [activeTab, setActiveTab] = useState('activite'); // 'activite' ou 'ressources'
  const sidebarRef = useRef(null);

  // Refs pour chaque élément de navigation
  const itemRefs = useRef({});

  // Items liés à l'activité
  const activiteItems = [
    { name: "Mon espace personnel", icon: <IoPersonOutline size={16} />, route: "/espace-personnel" },
    { name: "Mon patrimoine", icon: <IoStatsChartOutline size={16} />, route: "/espace-patrimoine" },
    { name: "Mon réseau", icon: <Users size={16} />, route: "/espace-reseaux" },
    { name: "Mes processus", icon: <GoGear size={16} />, route: "/espace-processus" },
    { name: "Mes opérations", icon: <HiOutlineBuildingOffice2 size={16} />, route: "/espace-operations" },
  ];

  // Items liés aux ressources
  const ressourcesItems = [
    { name: "Mon équipe d'agents IA", icon: <PiRobot size={16} />, route: "/equipe-agents" },
    { name: "Mes formations", icon: <TbSchool size={16} />, route: "/school" },
  ];

  const currentItems = activeTab === 'activite' ? activiteItems : ressourcesItems;

  // Effect to detect clicks outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && !collapsed) {
        setCollapsed(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [collapsed]);

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  // Nouvelle fonction pour gérer le clic sur la sidebar fermée
  const handleSidebarClick = (event) => {
    if (collapsed) {
      // Empêcher la propagation pour éviter les conflits
      event.stopPropagation();
      setCollapsed(false);
    }
  };

  const handleNavigation = (route) => {
    navigate(route);
  };

  const NavItem = ({ icon, label, onClick, isActive, itemKey }) => {
    const [isHovered, setIsHovered] = useState(false);

    const baseStyle = {
      display: 'flex',
      alignItems: 'center',
      padding: collapsed ? '8px' : '8px 12px',
      marginBottom: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.15s ease-in-out',
      borderRight: isActive ? '3px solid #10b981' : 'none',
      justifyContent: collapsed ? 'center' : 'flex-start'
    };

    const getItemStyle = () => {
      if (isActive) {
        return {
          ...baseStyle,
          background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',
          color: 'white',
          fontWeight: '600'
        };
      }
      if (isHovered) {
        return {
          ...baseStyle,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          color: 'white'
        };
      }
      return {
        ...baseStyle,
        color: 'rgba(255, 255, 255, 0.85)'
      };
    };

    const handleClick = (event) => {
      event.stopPropagation(); // Empêcher la propagation du clic
      if (collapsed) {
        // Si la sidebar est fermée, l'ouvrir ET exécuter l'action
        setCollapsed(false);
        onClick(); // Exécuter immédiatement l'action (navigation)
      } else {
        // Si elle est ouverte, exécuter l'action normale
        onClick();
      }
    };

    return (
      <li
        style={getItemStyle()}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={{
          flexShrink: 0,
          marginRight: collapsed ? 0 : '12px',
          color: isActive ? '#10b981' : 'inherit'
        }}>
          {icon}
        </div>
        {!collapsed && (
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {label}
          </span>
        )}
      </li>
    );
  };

  const TabButton = ({ isActive, onClick, children, tabKey }) => {
    const [isHovered, setIsHovered] = useState(false);

    const getTabStyle = () => {
      const baseStyle = {
        flex: 1,
        padding: collapsed ? '6px 3px' : '8px 8px',
        cursor: 'pointer',
        fontSize: collapsed ? '10px' : '11px',
        fontWeight: '500',
        textAlign: 'center',
        transition: 'color 0.2s ease-in-out',
        borderRadius: '4px',
        border: 'none',
        outline: 'none',
        position: 'relative',
        zIndex: 2,
        lineHeight: '1.2'
      };

      if (isActive) {
        return {
          ...baseStyle,
          backgroundColor: 'transparent',
          color: '#374151',
          fontWeight: '600'
        };
      }

      if (isHovered) {
        return {
          ...baseStyle,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          color: '#e5e7eb'
        };
      }

      return {
        ...baseStyle,
        backgroundColor: 'transparent',
        color: 'rgba(255, 255, 255, 0.7)'
      };
    };

    const handleClick = (event) => {
      event.stopPropagation(); // Empêcher la propagation du clic
      if (collapsed) {
        // Si la sidebar est fermée, l'ouvrir d'abord
        setCollapsed(false);
      } else {
        // Si elle est ouverte, changer d'onglet
        onClick();
      }
    };

    return (
      <button
        style={getTabStyle()}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {collapsed ? (children.charAt(0).toUpperCase()) : children}
      </button>
    );
  };

  return (
    <div
      ref={sidebarRef}
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        // GRADIENT PREMIUM avec subtile transition vers doré
        background: 'linear-gradient(180deg, #2a5a58 0%, #2e5e5c 12%, #32625f 25%, #366663 35%, #3a6a66 45%, #3e6e69 55%, #42726d 65%, #407670 75%, #3e7a73 85%, #3c7e76 95%, #3a8279 100%)',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.3s ease-in-out',
        width: collapsed ? '56px' : '256px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        cursor: collapsed ? 'pointer' : 'default' // Ajouter un curseur pointer quand fermée
      }}
      data-sidebar
      onClick={handleSidebarClick} // Ajouter le gestionnaire de clic
      onMouseLeave={() => {
        // Optionnel : ne rien faire ou autre logique
      }}
    >
      {/* Zone de redimensionnement avec curseur */}
      <ResizeHandle collapsed={collapsed} onToggle={toggleSidebar} />

      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header avec logo - SÉPARÉ DU TRAIT */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: '12px 16px 8px 16px', // Padding augmenté en haut pour descendre le logo
          minHeight: '40px'
        }}>
          <div
            onMouseEnter={() => {
              // Plus de tooltip
            }}
            onMouseLeave={() => {
              // Plus de tooltip
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              width: '100%'
            }}
            onClick={(event) => {
              event.stopPropagation(); // Empêcher la propagation du clic
              if (collapsed) {
                setCollapsed(false);
              } else {
                handleNavigation('/dashboard');
              }
            }}
          >
            <div style={{
              width: collapsed ? '31px' : '28px',
              height: collapsed ? '31px' : '28px',
              borderRadius: '50%',
              border: '2px solid white',
              backgroundColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginLeft: '-3px',
              marginTop: "-7px"
            }}>
              <img
                src="/data/logo/Selery_small.png"
                alt="Selery Logo"
                style={{
                  width: collapsed ? '17px' : '17px',
                  height: collapsed ? '17px' : '17px',
                  borderRadius: '1px'
                }}
              />
            </div>
            {!collapsed && (
              <div style={{
                marginLeft: '8px',
                display: 'flex',
                alignItems: 'flex-end',
                height: '24px'
              }}>
                <h1 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'white',
                  margin: 2.5,
                  lineHeight: '1.2'
                }}>
                  Selery platform
                </h1>
              </div>
            )}
          </div>
        </div>
        
        {/* Trait horizontal séparé */}
        <div style={{
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          marginTop: "-5px"
        }}>
        </div>
        {/* Plus de tooltip pour le logo */}

        {/* Onglets Activité/Ressources style Pennylane */}
        <div style={{
          padding: '6px 12px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            gap: '2px',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '5px',
            padding: '3px',
            position: 'relative'
          }}>
            {/* Élément glissant pour l'animation horizontale */}
            <div
              style={{
                position: 'absolute',
                top: '3px',
                left: activeTab === 'activite' ? '3px' : '50%',
                width: 'calc(50% - 3px)',
                height: 'calc(100% - 6px)',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '4px',
                transition: 'left 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                zIndex: 1,
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
              }}
            />
            <TabButton
              isActive={activeTab === 'activite'}
              onClick={() => setActiveTab('activite')}
              tabKey="tab-activite"
            >
              Activité
            </TabButton>
            <TabButton
              isActive={activeTab === 'ressources'}
              onClick={() => setActiveTab('ressources')}
              tabKey="tab-ressources"
            >
              Ressources
            </TabButton>
          </div>
        </div>

        {/* Navigation */}
        <nav style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px 0'
        }}>
          <ul style={{
            listStyle: 'none',
            margin: 0,
            padding: 0
          }}>
            {currentItems.map((item) => (
              <NavItem
                key={item.name}
                itemKey={item.name}
                icon={item.icon}
                label={item.name}
                isActive={activeItem === item.name}
                onClick={() => {
                  setActiveItem(item.name);
                  handleNavigation(item.route);
                }}
              />
            ))}
          </ul>
        </nav>

        {/* Section support en bas */}
        <div style={{
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '12px'
        }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              color: 'rgba(255, 255, 255, 0.85)',
              transition: 'all 0.15s ease-in-out',
              justifyContent: collapsed ? 'center' : 'flex-start'
            }}
            onClick={(event) => {
              event.stopPropagation(); // Empêcher la propagation du clic
              if (collapsed) {
                setCollapsed(false);
              } else {
                setShowSupportPopup(!showSupportPopup);
              }
            }}
            onMouseEnter={() => {
              // Plus de tooltip
            }}
            onMouseLeave={() => {
              // Plus de tooltip
            }}
          >
            <div style={{
              flexShrink: 0,
              marginRight: collapsed ? 0 : '12px'
            }}>
              <FaQuestionCircle size={16} />
            </div>
            {!collapsed && <span>Aide & Support</span>}
          </div>
          {/* Plus de tooltip pour le support */}
        </div>
      </div>

      {/* Support popup */}
      <SupportPopup
        isOpen={showSupportPopup}
        onClose={() => setShowSupportPopup(false)}
      />
    </div>
  );
}

export default Sidebar;